import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { getPublishedNews, formatNewsDate } from '../../services/newsService';
import './NewsPage.css';

const NewsPage = () => {
  const navigate = useNavigate();
  const [newsArticles, setNewsArticles] = useState([]);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalNews, setTotalNews] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const itemsPerPage = 10;

  // Fetch news from API
  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const offset = (currentPage - 1) * itemsPerPage;
        const data = await getPublishedNews({ limit: itemsPerPage, offset });
        
        const articles = data.news || [];
        setNewsArticles(articles);
        setTotalNews(data.total || 0);
        setHasMore(data.hasMore || false);
        
        // Set related articles (different from main articles, up to 5)
        if (articles.length > 1) {
          setRelatedArticles(articles.slice(0, 5));
        }
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Failed to load news. Please try again later.');
        setNewsArticles([]);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, [currentPage]);

  const handleNewsClick = (article) => {
    navigate(`/news/${article.id}`);
  };

  const handleRelatedClick = (article) => {
    navigate(`/news/${article.id}`);
  };

  const handleLoadMore = () => {
    if (hasMore) {
      setCurrentPage(prev => prev + 1);
    }
  };

  // Helper to truncate text
  const truncateText = (text, maxLength = 200) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const ChevronRightIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  // Loading state
  if (loading && newsArticles.length === 0) {
    return (
      <div className="news-page">
        <Header />
        <main className="news-main">
          <div className="news-container">
            <div className="news-loading">
              <div className="loading-spinner"></div>
              <p>Loading news...</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Error state
  if (error && newsArticles.length === 0) {
    return (
      <div className="news-page">
        <Header />
        <main className="news-main">
          <div className="news-container">
            <div className="news-error">
              <p>{error}</p>
              <button onClick={() => setCurrentPage(1)} className="retry-btn">Try Again</button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="news-page">
      <Header />
      
      <main className="news-main">
        <div className="news-container">
          {/* Breadcrumb */}
          <div className="news-breadcrumb">
            <Link to="/" className="breadcrumb-link">Home</Link>
            <ChevronRightIcon />
            <span className="breadcrumb-current">News</span>
          </div>

          {/* Page Title */}
          <div className="news-page-header">
            <h1 className="news-page-title">Latest Construction News</h1>
            <p className="news-page-subtitle">Stay updated with the latest news from the construction industry</p>
          </div>

          {/* Content Section */}
          <div className="news-content-wrapper">
            <div className="news-content-section">
              {/* Main Content */}
              <div className="news-main-content">
                {newsArticles.length === 0 ? (
                  <div className="no-news">
                    <p>No news articles available at the moment.</p>
                  </div>
                ) : (
                  newsArticles.map((article, index) => (
                    <article 
                      key={article.id} 
                      className={`news-article ${index === 0 ? 'news-article--featured' : ''}`}
                      onClick={() => handleNewsClick(article)}
                    >
                      {/* Article Image */}
                      {article.image && (
                        <div className="article-img-wrap">
                          <img
                            src={article.image}
                            alt={article.title}
                            className="article-img"
                            onError={(e) => { e.target.closest('.article-img-wrap').style.display = 'none'; }}
                          />
                          <span className="article-category-badge">{article.category || 'News'}</span>
                        </div>
                      )}
                      {!article.image && (
                        <div className="article-no-img">
                          <span className="article-category-badge article-category-badge--standalone">{article.category || 'News'}</span>
                        </div>
                      )}

                      {/* Article Body */}
                      <div className="article-body">
                        <h2 className="article-title">{article.title}</h2>

                        <div className="article-meta">
                          <span className="article-meta__date">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="18" rx="2" stroke="#6D6D6D" strokeWidth="1.8"/><path d="M16 2v4M8 2v4M3 10h18" stroke="#6D6D6D" strokeWidth="1.8" strokeLinecap="round"/></svg>
                            {formatNewsDate(article.publishedAt || article.createdAt)}
                          </span>
                          {article.authorName && (
                            <span className="article-meta__author">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke="#1F396C" strokeWidth="1.8"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="#1F396C" strokeWidth="1.8" strokeLinecap="round"/></svg>
                              {article.authorName}
                            </span>
                          )}
                          <span className="article-meta__views">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#6D6D6D" strokeWidth="1.8"/><circle cx="12" cy="12" r="3" stroke="#6D6D6D" strokeWidth="1.8"/></svg>
                            {article.views || 0} views
                          </span>
                        </div>

                        <p className="article-excerpt">
                          {truncateText(article.excerpt || article.content, index === 0 ? 280 : 180)}
                        </p>

                        <div className="article-footer">
                          <span className="read-more-btn">
                            Read More
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </span>
                        </div>
                      </div>
                    </article>
                  ))
                )}

                {/* Load More Button */}
                {hasMore && (
                  <div className="load-more-container">
                    <button className="load-more-btn" onClick={handleLoadMore} disabled={loading}>
                      {loading ? 'Loading...' : 'Load More News'}
                    </button>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <aside className="news-sidebar">
                <div className="sidebar-sticky">
                  <h3 className="sidebar-title">
                    <span className="sidebar-title__bar"></span>
                    Related Articles
                  </h3>
                  <div className="related-articles">
                    {relatedArticles.length > 0 ? (
                      relatedArticles.map((article) => (
                        <div
                          key={article.id}
                          className="related-article"
                          onClick={() => handleRelatedClick(article)}
                        >
                          {article.image && (
                            <div className="related-article__img-wrap">
                              <img src={article.image} alt={article.title} onError={(e) => { e.target.closest('.related-article__img-wrap').style.display = 'none'; }} />
                            </div>
                          )}
                          <div className="related-article__info">
                            {article.category && <span className="related-category">{article.category}</span>}
                            <h4 className="related-title">{truncateText(article.title, 70)}</h4>
                            <span className="related-date">{formatNewsDate(article.publishedAt || article.createdAt)}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="no-related">No related articles</p>
                    )}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NewsPage;
